// Speech recorder component for speech-to-text functionality
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Mic, Square, ChevronDown } from "lucide-react";

const SpeechRecorder = ({
  value,
  onTranscriptChange,
  onListeningChange,
  className = "",
  render,
}) => {
  const recognitionRef = useRef(null);
  const languageDropdownRef = useRef(null);
  const languageButtonRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const isListeningRef = useRef(false); // Ref to track listening state in closures
  const [selectedLanguage, setSelectedLanguage] = useState("auto"); // Default to auto-detect
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const baseTextRef = useRef("");
  const committedRef = useRef("");

  // Available languages - English, Arabic, Spanish, and Portuguese
  const availableLanguages = [
    { code: "auto", name: "Auto", flag: "🌐" },
    { code: "en-US", name: "English", flag: "🇺🇸" },
    { code: "ar-SA", name: "العربية", flag: "🇸🇦" },
    { code: "es-ES", name: "Español", flag: "🇪🇸" },
    { code: "pt-BR", name: "Português", flag: "🇧🇷" },
  ];

  // Language detection: Map browser language codes to our supported languages
  const detectBrowserLanguage = useCallback(() => {
    if (typeof navigator === "undefined") return "en-US";
    
    // Get browser language preferences
    const browserLang = navigator.language || navigator.userLanguage;
    const browserLangs = navigator.languages || [browserLang];
    
    // Map common language codes to our supported languages
    const languageMap = {
      // English variants
      "en": "en-US",
      "en-us": "en-US",
      "en-gb": "en-US",
      "en-ca": "en-US",
      "en-au": "en-US",
      // Arabic variants
      "ar": "ar-SA",
      "ar-sa": "ar-SA",
      "ar-eg": "ar-SA",
      "ar-ae": "ar-SA",
      // Spanish variants
      "es": "es-ES",
      "es-es": "es-ES",
      "es-mx": "es-ES",
      "es-ar": "es-ES",
      "es-co": "es-ES",
      "es-cl": "es-ES",
      "es-pe": "es-ES",
      "es-ve": "es-ES",
      // Portuguese variants
      "pt": "pt-BR",
      "pt-br": "pt-BR",
      "pt-pt": "pt-BR",
    };
    
    // Try to find a match in browser languages
    for (const lang of browserLangs) {
      const langCode = lang.toLowerCase();
      // Check exact match
      if (languageMap[langCode]) {
        return languageMap[langCode];
      }
      // Check language prefix (e.g., "es-MX" -> "es")
      const langPrefix = langCode.split("-")[0];
      if (languageMap[langPrefix]) {
        return languageMap[langPrefix];
      }
    }
    
    // Default to English if no match found
    return "en-US";
  }, []);

  // Get the actual language code to use (resolve "auto" to detected language)
  const effectiveLanguage = useMemo(() => {
    if (selectedLanguage === "auto") {
      return detectBrowserLanguage();
    }
    return selectedLanguage;
  }, [selectedLanguage, detectBrowserLanguage]);

  // Get current language info
  const getCurrentLanguageInfo = () => {
    if (selectedLanguage === "auto") {
      const detectedLang = detectBrowserLanguage();
      const detectedLangInfo = availableLanguages.find(
        (lang) => lang.code === detectedLang
      );
      return {
        code: "auto",
        name: `Auto (${detectedLangInfo?.name || "English"})`,
        flag: "🌐",
        detectedCode: detectedLang,
      };
    }
    return (
      availableLanguages.find((lang) => lang.code === selectedLanguage) ||
      availableLanguages[1] // Default to English if not found
    );
  };

  // Initialize speech recognition
  const ensureRecognition = () => {
    if (recognitionRef.current) return recognitionRef.current;
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;
    const recognition = new SpeechRecognition();
    recognition.lang = effectiveLanguage;
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    // Handle speech recognition results
    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          committedRef.current = [
            committedRef.current,
            res[0].transcript.trim(),
          ]
            .filter(Boolean)
            .join(" ");
        } else {
          interim = [interim, res[0].transcript.trim()]
            .filter(Boolean)
            .join(" ");
        }
      }
      const combined = [baseTextRef.current, committedRef.current, interim]
        .filter(Boolean)
        .join(" ");
      onTranscriptChange?.(combined);
    };

    // Handle speech recognition errors
    recognition.onerror = (event) => {
      isListeningRef.current = false;
      setIsListening(false);
    };

    // Handle speech recognition end - keep recording running even during silence
    recognition.onend = () => {
      // Use ref to check current listening state (avoids stale closure)
      if (isListeningRef.current && recognitionRef.current) {
        // Use setTimeout to ensure proper restart after the end event completes
        setTimeout(() => {
          // Double-check state before restarting
          if (isListeningRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch (error) {
              // If restart fails, try again after a short delay
              setTimeout(() => {
                if (isListeningRef.current && recognitionRef.current) {
                  try {
                    recognitionRef.current.start();
                  } catch (_) {
                    // Only stop if restart fails multiple times
                    isListeningRef.current = false;
                    setIsListening(false);
                  }
                }
              }, 100);
            }
          }
        }, 10);
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  };

  // Update recognition language when selectedLanguage changes
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = effectiveLanguage;
    }
  }, [effectiveLanguage]);

  // Toggle speech recognition
  const toggle = async () => {
    const recognition = ensureRecognition();
    if (!recognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    if (isListening) {
      // Stop speech recognition
      try {
        isListeningRef.current = false;
        // Don't remove onend handler - it checks isListeningRef, so it won't restart
        recognition.stop();
      } catch (_) {}
      setIsListening(false);
      return;
    }

    // Start speech recognition
    baseTextRef.current = (value || "").trim();
    committedRef.current = "";
    recognition.lang = effectiveLanguage;
    isListeningRef.current = true;

    // Start speech recognition
    try {
      recognition.start();
    } catch (_) {}
    setIsListening(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is on language button or dropdown (including data attributes)
      const isLanguageButton = event.target.closest("[data-language-button]");
      const isLanguageDropdown = event.target.closest(
        "[data-language-dropdown]"
      );

      // If clicked outside both button and dropdown, close the menu
      if (!isLanguageButton && !isLanguageDropdown) {
        setShowLanguageMenu(false);
      }
    }

    if (showLanguageMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLanguageMenu]);

  // Notify parent component of listening state changes
  useEffect(() => {
    onListeningChange?.(isListening);
  }, [isListening, onListeningChange]);

  // Cleanup on component unmount
  useEffect(
    () => () => {
      try {
        isListeningRef.current = false;
        recognitionRef.current?.stop?.();
      } catch (_) {}
      recognitionRef.current = null;
    },
    []
  );

  // Use render prop if provided
  if (typeof render === "function") {
    return render({
      isListening,
      toggle,
      selectedLanguage,
      setSelectedLanguage,
      availableLanguages,
      showLanguageMenu,
      setShowLanguageMenu,
      getCurrentLanguageInfo,
    });
  }

  // Default rendering with language selector
  const currentLangInfo = getCurrentLanguageInfo();

  return (
    <div className="flex items-center gap-1 relative">
      {/* Language selector button */}
      <button
        ref={languageButtonRef}
        type="button"
        className="flex items-center gap-1 p-1.5 rounded-lg text-fg-50 hover:bg-bg-50/20 transition-colors text-xs"
        title={`Speech language: ${currentLangInfo.name}`}
        onClick={() => setShowLanguageMenu(!showLanguageMenu)}
      >
        <span className="text-sm">{currentLangInfo.flag}</span>
        <ChevronDown size={12} />
      </button>

      {/* Language dropdown */}
      {showLanguageMenu && (
        <div
          ref={languageDropdownRef}
          className="absolute bottom-full right-0 mb-2 bg-bg-60 rounded-lg shadow-xl border border-bd-50 min-w-[140px] z-50"
        >
          <div className="px-3 py-2 bg-bg-50 text-xs font-medium text-fg-50 border-b border-bd-50">
            Language
          </div>
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                selectedLanguage === lang.code
                  ? "bg-bg-50 text-ac-02"
                  : "text-fg-50 hover:bg-bg-50"
              }`}
              onClick={() => {
                setSelectedLanguage(lang.code);
                setShowLanguageMenu(false);
              }}
            >
              <span className="text-base">{lang.flag}</span>
              <span className="text-xs">{lang.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Mic button */}
      <button
        type="button"
        className={`p-2 rounded-lg transition-colors ${
          isListening
            ? "bg-ac-02 text-white dark:text-black hover:bg-ac-01"
            : "text-fg-50 hover:bg-bg-50/20"
        } ${className}`}
        title={
          isListening
            ? "Stop recording"
            : `Record audio (${currentLangInfo.name})`
        }
        onClick={toggle}
        aria-pressed={isListening}
      >
        {isListening ? (
          <Square size={18} fill="currentColor" strokeWidth={0} />
        ) : (
          <Mic size={18} strokeWidth={1} />
        )}
      </button>
    </div>
  );
};

export default SpeechRecorder;
